import React, { useEffect, useState } from 'react';
import './AddRequisiteModal.css';
import DetailsPortal from './DetailsPortal';

const INITIAL_FORM = {
  amount: ''
};

const AddBalanceRequestModal = ({ isOpen, trader, gateway, onClose, onSubmit }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [amountSign, setAmountSign] = useState('+');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM);
      setAmountSign('+');
      setSubmitting(false);
      setError('');
    }
  }, [isOpen]);

  const entity = gateway || trader;
  const isGateway = Boolean(gateway);

  if (!isOpen || !entity) {
    return null;
  }

  const entityId = isGateway
    ? entity.gatewayId || entity.GatewayId || entity.GATEWAY_ID || entity.id || entity.Id || entity.ID || entity.ref || ''
    : entity.traderId || entity.TraderId || entity.TRADER_ID || entity.ref || '';
  const entityName = entity.name || entity.Name || entity.NAME || '-';
  const entityLabel = isGateway ? 'Gateway' : 'Trader';

  const handleClose = () => {
    if (!submitting) {
      setFormData(INITIAL_FORM);
      setAmountSign('+');
      setError('');
      onClose();
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'amount' && !/^\d*(?:\.\d{0,2})?$/.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const amount = String(formData.amount).trim();
    const amountNum = Number(amount);

    if (!amount) {
      setError('Amount is required');
      return;
    }

    if (!Number.isFinite(amountNum) || amountNum === 0) {
      setError('Amount must be a non-zero number');
      return;
    }

    const signedAmount = amountSign === '-' ? `-${amount}` : amount;

    setSubmitting(true);

    try {
      const result = await onSubmit({
        amount: signedAmount,
        [isGateway ? 'gateway' : 'trader']: entity
      });

      if (!result?.success) {
        setError(result?.error || 'Failed to create request');
        return;
      }

      onClose();
    } catch (submitError) {
      setError(submitError.message || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DetailsPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(event) => event.stopPropagation()}>
          <div className="modal-header">
            <div>
              <h2>Add Balance Request</h2>
              <p style={{ margin: '8px 0 0', color: 'rgba(247, 251, 255, 0.82)' }}>
                Create a new balance change request for {entityName}.
              </p>
            </div>
            <button
              type="button"
              className="modal-close"
              onClick={handleClose}
              disabled={submitting}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </div>

          <form className="requisite-form" onSubmit={handleSubmit}>
            {error && <div className="form-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="balance-request-entity-id">{entityLabel} ID</label>
              <input
                id="balance-request-entity-id"
                type="text"
                className="form-input"
                value={entityId}
                disabled
              />
            </div>

            <div className="form-group">
              <label htmlFor="balance-request-amount">
                Amount, USDT <span className="required">*</span>
              </label>
              <div className="balance-amount-control">
                <div className="balance-sign-toggle" aria-label="Balance operation">
                  <button
                    type="button"
                    className={`balance-sign-button ${amountSign === '+' ? 'is-active' : ''}`}
                    aria-pressed={amountSign === '+'}
                    onClick={() => setAmountSign('+')}
                    disabled={submitting}
                  >
                    +
                  </button>
                  <button
                    type="button"
                    className={`balance-sign-button balance-sign-minus ${amountSign === '-' ? 'is-active' : ''}`}
                    aria-pressed={amountSign === '-'}
                    onClick={() => setAmountSign('-')}
                    disabled={submitting}
                  >
                    −
                  </button>
                </div>
                <input
                  id="balance-request-amount"
                  name="amount"
                  type="text"
                  inputMode="decimal"
                  className="form-input"
                  value={formData.amount}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="1000.00"
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Request'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </DetailsPortal>
  );
};

export default AddBalanceRequestModal;
